import { useEffect } from 'react'
import { buildHeadTags, getPageSeo, type PageSeo } from './pages'
import { THEME_COLOR } from './site'

function ensureMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function ensureLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function ensureJsonLd(id: string, data: unknown) {
  let el = document.getElementById(id) as HTMLScriptElement | null
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

export function applyPageSeo(page: PageSeo, jsonLd?: unknown | unknown[]) {
  const tags = buildHeadTags(page)
  document.title = tags.title
  document.documentElement.lang = 'en'
  ensureMeta('name', 'description', tags.description)
  ensureMeta('name', 'robots', tags.robots)
  ensureMeta('name', 'theme-color', THEME_COLOR)
  ensureLink('canonical', tags.canonical)
  for (const [k, v] of Object.entries(tags.og)) ensureMeta('property', k, v)
  for (const [k, v] of Object.entries(tags.twitter)) ensureMeta('name', k, v)

  if (jsonLd !== undefined) {
    const payload = Array.isArray(jsonLd) ? jsonLd : [jsonLd]
    ensureJsonLd('pp-jsonld', payload.length === 1 ? payload[0] : payload)
  }
}

type SeoHeadProps = {
  path: string
  jsonLd?: unknown | unknown[]
  /** Optional override when path lookup is insufficient */
  page?: PageSeo
}

/** Client-side head sync for hydration after prerendered HTML. */
export function SeoHead({ path, jsonLd, page }: SeoHeadProps) {
  useEffect(() => {
    const resolved = page || getPageSeo(path)
    if (!resolved) return
    applyPageSeo(resolved, jsonLd)
  }, [path, page, jsonLd])

  return null
}
